import { FC } from 'react'
import { Monitor, User, MapPin, Calendar } from 'lucide-react'
import { SessionReport } from '@facesignai/api'

interface Props {
  report: SessionReport
  thumbnailUrl?: string
}

const SessionSummaryCard: FC<Props> = ({ report, thumbnailUrl }) => {
  if (!report) {
    return null
  }

  // Extract session summary data
  const device = report.device
  const deviceInfo = device
    ? `${device.platform}, ${device.browser}`
    : 'Unknown device'

  // Extract age and gender from aiAnalysis
  const aiAnalysis = report.aiAnalysis
  const ageInfo =
    aiAnalysis?.ageMin && aiAnalysis?.ageMax
      ? `${aiAnalysis.ageMin}-${aiAnalysis.ageMax} years old`
      : undefined
  const rawGender = aiAnalysis?.sex
  const genderInfo = rawGender
    ? rawGender.charAt(0).toUpperCase() + rawGender.slice(1)
    : undefined

  // Extract location
  const location = report.location
  const locationInfo =
    location?.city?.names?.en && location?.country?.names?.en
      ? `${location.city.names.en}, ${location.country.names.en}`
      : location?.country?.names?.en || 'Not available'

  return (
    <div className='bg-white rounded-lg shadow-sm p-4 mb-6'>
      <div className='flex gap-4'>
        {/* User thumbnail */}
        {thumbnailUrl && (
          <div className='flex-shrink-0'>
            <img
              src={thumbnailUrl}
              alt='User'
              className='w-16 h-16 rounded-lg object-cover'
            />
          </div>
        )}
        {/* Session info grid */}
        <div className='flex-1 grid grid-cols-2 gap-x-6 gap-y-3'>
          {ageInfo && (
            <div className='flex items-center gap-2'>
              <Calendar size={16} className='text-gray-500 flex-shrink-0' />
              <p className='text-sm text-gray-700'>{ageInfo}</p>
            </div>
          )}
          {genderInfo && (
            <div className='flex items-center gap-2'>
              <User size={16} className='text-gray-500 flex-shrink-0' />
              <p className='text-sm text-gray-700'>{genderInfo}</p>
            </div>
          )}
          <div className='flex items-center gap-2'>
            <MapPin size={16} className='text-gray-500 flex-shrink-0' />
            <p className='text-sm text-gray-700'>{locationInfo}</p>
          </div>
          <div className='flex items-center gap-2'>
            <Monitor size={16} className='text-gray-500 flex-shrink-0' />
            <p className='text-sm text-gray-700'>{deviceInfo}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SessionSummaryCard
